import _get from 'lodash/get'
import _parseInt from 'lodash/parseInt'
import _filter from 'lodash/filter'
import { List } from 'immutable'
import { SUBSCRIPTION_CODE_PURCHASE_ONLY } from 'services/subscription'

/**
 * Filter entitlements to only those whose end date (Unix seconds) has not passed.
 * Preserves original array order. Used by get() with plain array from API.
 * @param {Array<{end?: string|number}>} entitlementsArray - entitlement objects
 * @returns {Array} new array of unexpired entitlements, same order
 */
export function filterUnexpiredEntitlements (entitlementsArray) {
  const nowMs = Date.now()
  return _filter(entitlementsArray || [], (entitlement) => {
    const endStr = _get(entitlement, 'end')
    if (!endStr) return true
    const endSeconds = _parseInt(endStr, 10)
    if (Number.isNaN(endSeconds)) return true
    return endSeconds * 1000 > nowMs
  })
}

/**
 * Filter out content_set_id === SUBSCRIPTION_CODE_PURCHASE_ONLY and truthy ecommerce_sku.
 * Preserves original order. Accepts and returns Immutable List (for Redux state).
 * @param {List} entitlementsList - List of entitlement Maps
 * @returns {List} new List of eligible entitlements, same order
 */
export function filterEntitlementsByContentSetAndSku (entitlementsList = List()) {
  if (!List.isList(entitlementsList)) {
    return List()
  }
  return entitlementsList.filter((entitlement) => {
    const contentSetId = entitlement.get('content_set_id')
    const ecommerceSku = entitlement.get('ecommerce_sku')
    if (contentSetId === SUBSCRIPTION_CODE_PURCHASE_ONLY) return false
    if (ecommerceSku) return false
    return true
  })
}

/**
 * Returns true if every entitlement in the list is either purchase-only
 * (content_set_id === SUBSCRIPTION_CODE_PURCHASE_ONLY) or has a non-falsy ecommerce_sku.
 * Empty list returns true.
 * @param {List} entitlementsList - List of entitlement Maps
 * @returns {boolean}
 */
export function areEntitlementsPurchaseOnlyOrEcommerce (entitlementsList = List()) {
  if (!List.isList(entitlementsList) || entitlementsList.isEmpty()) {
    return false
  }

  return entitlementsList.every((entitlement) => {
    const contentSetId = entitlement.get('content_set_id')
    const ecommerceSku = entitlement.get('ecommerce_sku')
    return contentSetId === SUBSCRIPTION_CODE_PURCHASE_ONLY || Boolean(ecommerceSku)
  })
}
